//@flow
import TrackJs from './trackjs-interface';
import NumberUtils from './number';

import type ProductModel from '../Stores/Products/productModel';
import type QuoteItemsCollection from '../Stores/Quote/quoteItemsCollection';

export type Product = {
  name:string,
  id:string,
  price?:?string,
  brand?:string,
  category?:string,
  variant?:string,
  position?:number,
  list?:string,
  quantity?:number,
};

export type ProductClick = {
  actionField?:{ list:string },
  products:Product[],
};

export type ProductDetail = ProductClick;

export type CartEvent = {
  products:Product[],
};

export type Promotion = {
  id:string,
  name:string,
  creative?:string,
  position?:string,
};

export type PromoView = {
  promotions:Promotion[],
};

export type CheckoutOption  = {
  step:number,
  option?:string,
};

export type Checkout = {
  actionField?:CheckoutOption,
  products:Product[],
};

export type Purchase = {
  actionField:{
    id:string,
    affiliation?:string,
    revenue:string,
    tax?:string,
    shipping?:string,
    coupon?:?string,
  },
  products:Product[],
};

export type Event = 'identify'|'success'|'productClick'|'addToCart'|'removeFromCart'|'checkout'|'checkoutOption'|'purchase'|'pageView'|'officeFormSuccess'|'virtualPageView';

export type Ecommerce = {
  currencyCode?:string,
  impressions?:Product[],
  click?:ProductClick,
  detail?:ProductDetail,
  add?:CartEvent,
  remove?:CartEvent,
  promoView?:PromoView,
  checkout?:Checkout,
  checkout_option?:{ actionField:CheckoutOption },
  purchase?:Purchase,
};

export type Customer = {
  email:?string,
};

export type DataObject = {
  event?:Event,
  page?:string,
  customer?:Customer,
  ecommerce?:Ecommerce,
  order?:Object,
  eventCallback?:() => void,
  pageUrl?:string,
  pagePath?:string,
};

function _addToDataLayer(obj:DataObject) {
  try {
    if (global.inServer) return;

    if (!global.dataLayer) {
      global.dataLayer = [];
    }

    global.dataLayer.push(obj);
  } catch (e) {
    TrackJs.console.debug(`error: ${e}`);
    TrackJs.track("GTM error");
  }
}

class GoogleTagManager {
  ecommerceEvent(ecommerce:$Shape<Ecommerce>, event?:Event, callback?:() => void) {
    const data:DataObject = { ecommerce };
    if (event) data.event = event;
    if (callback) data.eventCallback = callback;
    _addToDataLayer(data);
  }

  generalEvent(data:DataObject) {
    _addToDataLayer(data);
  }

  buildProductData(product:ProductModel):Product {
    return {
      name: product.getName(),
      id: product.getSku(),
      price: product.getMemberPrice(),
    };
  }

  trackCheckoutStep(quoteItems:QuoteItemsCollection, step:number) {
    const products = quoteItems.models.map((item) => {
      return {
        name: item.getName(),
        id: item.getSku(),
        price: NumberUtils.toFixed(item.getPrice()),
        quantity: item.getQty(),
      };
    });

    this.ecommerceEvent({
      checkout: {
        actionField: { step },
        products,
      },
    }, 'checkout');
  }
}

export default new GoogleTagManager();
